import { Trans } from "@lingui/react/macro"
import { Alert, Button, Group, List, Loader, Stack, Text } from "@mantine/core"
import { AuthShell } from "../../-auth-shared/AuthShell"
import type { IProps } from "./interfaces"

/**
 * OAuth consent card for Connect Claude. Pure presentation: says who is asking,
 * what they get, and offers Allow / Deny. The smart route owns the API calls.
 */
export const ConsentView = ({
  logo,
  appName,
  product = "lexicanon",
  loginBackground,
  clientName,
  redirectHosts = [],
  userEmail,
  state,
  error,
  onAllow,
  onDeny,
}: IProps): React.JSX.Element => {
  const name = clientName?.trim() || "An app"
  const productName = appName ?? (product === "voxcanon" ? "Voxcanon" : "Lexicanon")

  if (state === "loading") {
    return (
      <AuthShell
        logo={logo}
        appName={appName}
        product={product}
        loginBackground={loginBackground}
        title={<Trans>Connect</Trans>}
      >
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      </AuthShell>
    )
  }

  if (state === "invalid") {
    return (
      <AuthShell
        logo={logo}
        appName={appName}
        product={product}
        loginBackground={loginBackground}
        title={<Trans>This connection request has expired</Trans>}
        subtitle={<Trans>Start again from the app that asked to connect.</Trans>}
      >
        <Button component="a" href="/" variant="default" fullWidth>
          <Trans>Back to {productName}</Trans>
        </Button>
      </AuthShell>
    )
  }

  const submitting = state === "submitting"

  return (
    <AuthShell
      logo={logo}
      appName={appName}
      product={product}
      loginBackground={loginBackground}
      title={
        <Trans>
          Let {name} use your {productName} account?
        </Trans>
      }
      subtitle={userEmail ? <Trans>Signed in as {userEmail}</Trans> : undefined}
    >
      <Stack gap="md">
        <div>
          <Text size="sm" fw={600} mb={4}>
            <Trans>{name} will be able to</Trans>
          </Text>
          <List size="sm" spacing={4}>
            <List.Item>
              <Trans>See which workspaces you belong to</Trans>
            </List.Item>
            <List.Item>
              <Trans>Read the meetings, summaries and transcripts you can see</Trans>
            </List.Item>
            <List.Item>
              <Trans>Ask questions about a meeting (this uses your workspace's credits)</Trans>
            </List.Item>
          </List>
          <Text size="sm" c="dimmed" mt={6}>
            <Trans>It cannot change or delete anything.</Trans>
          </Text>
        </div>

        {redirectHosts.length > 0 ? (
          <Text size="xs" c="dimmed">
            <Trans>The sign-in code goes to {redirectHosts.join(", ")}.</Trans>
          </Text>
        ) : null}

        {error ? (
          <Alert color="danger" variant="light">
            {error}
          </Alert>
        ) : null}

        <Group grow>
          <Button variant="default" onClick={onDeny} disabled={submitting}>
            <Trans>Deny</Trans>
          </Button>
          <Button color="brand" onClick={onAllow} loading={submitting}>
            <Trans>Allow</Trans>
          </Button>
        </Group>
      </Stack>
    </AuthShell>
  )
}
